/**
 * O3 (SO-38) — the Zod schemas behind the owner forms (business / branch /
 * service) carry English messages. Rather than rewrite the schemas (shared with
 * other surfaces and tests), the owner screens pass the message through here to
 * show the localized copy from the `mb.validation.*` keys. Unknown messages are
 * returned unchanged.
 */
type TFn = (key: string) => string;

const MESSAGE_KEYS: Record<string, string> = {
    // business.schema.ts
    'Business name is required': 'mb.validation.businessNameRequired',
    'Arabic business name is required': 'mb.validation.businessNameArRequired',
    'Arabic name is too long (max 200 characters)': 'mb.validation.businessNameArTooLong',
    'About your business is required (min 50 characters)': 'mb.validation.aboutRequired',
    'About is too long (max 2000 characters)': 'mb.validation.aboutTooLong',
    'نبذة بالعربية مطلوبة (50 حرف على الأقل) / Arabic about is required (min 50 chars)': 'mb.validation.aboutArRequired',
    'النبذة طويلة جداً / Arabic about is too long': 'mb.validation.aboutArTooLong',
    'Country is required': 'mb.validation.countryRequired',
    'Invalid email address': 'mb.validation.emailInvalid',
    'End date must be on or after start date': 'mb.validation.endBeforeStart',
    // branch.schema.ts
    'Branch Name must be at least 3 characters and up to 100 alphanumeric characters.': 'mb.validation.branchName',
    'Street address must be at least 5 characters.': 'mb.validation.streetMin',
    'Street address cannot exceed 200 characters.': 'mb.validation.streetMax',
    'City is required': 'mb.validation.cityRequired',
    'City cannot exceed 100 characters.': 'mb.validation.cityMax',
    'State cannot exceed 100 characters.': 'mb.validation.stateMax',
    'Country cannot exceed 100 characters.': 'mb.validation.countryMax',
    'Invalid entry. Please check your input.': 'mb.validation.invalidEntry',
    'Invalid phone number. Please enter a valid number.': 'mb.validation.phoneInvalid',
    // ServiceAddEditModal schema
    'Service name must be at least 2 characters': 'mb.validation.serviceNameMin',
    'Service name cannot exceed 100 characters': 'mb.validation.serviceNameMax',
    'Branch is required': 'mb.validation.branchRequired',
    'Duration is required': 'mb.validation.durationRequired',
    'Description cannot exceed 1000 characters': 'mb.validation.descriptionMax',
    'At least one pricing tier is required': 'mb.validation.tierRequired',
    'Tier name is required': 'mb.validation.tierNameRequired',
    'Tier name cannot exceed 100 characters': 'mb.validation.tierNameMax',
    'Price is required': 'mb.validation.priceRequired',
    'Price must be ≥ 0': 'mb.validation.priceMin',
};

export function translateValidationMessage(message: string | undefined, t: TFn): string | undefined {
    if (!message) return message;
    const key = MESSAGE_KEYS[message];
    return key ? t(key) : message;
}

/** Exposed for the string-module test: every mapped key must exist. */
export const VALIDATION_MESSAGE_KEYS = Object.values(MESSAGE_KEYS);
